import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Package, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SFGPutAway = () => {
  const navigate = useNavigate();
  const bundleInputRef = useRef(null);
  const staffInputRef = useRef(null);
  const binInputRef = useRef(null);

  const [staffId, setStaffId] = useState('');
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [staffName, setStaffName] = useState('');
  
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  
  const [binLocation, setBinLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [putAwaySuccess, setPutAwaySuccess] = useState(false);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  const handleStaffValidate = async () => {
    const id = staffId.trim();
    if (!id) {
      toast.error('Please enter Staff ID');
      return;
    }
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffId('');
      staffInputRef.current?.focus();
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${id}`);
      if (response.data.valid) {
        const nm = response.data.staff_name;
        setStaffList((prev) => [...prev, { staff_id: id, staff_name: nm }]);
        setStaffId('');
        toast.success(`Added ${nm}`);
        setTimeout(() => staffInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStaff = (id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== id));
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan or enter bundle code');
      return;
    }

    try {
      setLoading(true);
      setPutAwaySuccess(false);
      // Pass staff_id for location locking to prevent traffic congestion
      const response = await apiClient.post(`/wms/sfg/validate-bundle/${bundleCode}?staff_id=${encodeURIComponent(staffList[0]?.staff_id || '')}`);
      
      setBundleDetails(response.data.bundle);
      setSuggestedBin(response.data.suggested_bin);
      
      // Keep bin location input blank - user must manually enter/scan
      setBinLocation('');
      
      if (response.data.suggested_bin) {
        toast.success(`Bundle validated. Suggested bin: ${response.data.suggested_bin.bin_code}`);
      } else {
        toast.warning('Bundle validated but no bin suggestion available');
      }
      
      setTimeout(() => binInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle validation failed');
      setBundleDetails(null);
      setSuggestedBin(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePutAway = async () => {
    if (!bundleDetails) {
      toast.error('Please scan a bundle first');
      return;
    }
    if (!binLocation.trim()) {
      toast.error('Please enter bin location');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/wms/sfg/put-away', {
        staff_id: staffList[0]?.staff_id || '',
        staff_ids: staffList.map((s) => s.staff_id),
        bundle_code: bundleCode,
        bin_location: binLocation
      });

      toast.success(response.data.message);
      setPutAwaySuccess(true);
      
      // Reset for next bundle
      setTimeout(() => {
        setBundleCode('');
        setBundleDetails(null);
        setSuggestedBin(null);
        setBinLocation('');
        setPutAwaySuccess(false);
        bundleInputRef.current?.focus();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const resetAll = () => {
    setStaffId('');
    setStaffList([]);
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinLocation('');
    setPutAwaySuccess(false);
    staffInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/wms')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">SFG Put-Away</h1>
            <p className="text-sm text-[#E8DCC8]">Scan bundles and put away to storage</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        {/* Staff Validation (multi-staff) */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>

          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Staff ID</Label>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Scan or enter Staff ID (add one or more)"
                className="mt-1"
                data-testid="staff-id-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="staff-add-btn"
              >
                + Add
              </Button>
            </div>
          </div>

          {staffList.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="staff-list">
              {staffList.map((s) => (
                <div
                  key={s.staff_id}
                  className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full pl-3 pr-1 py-1"
                  data-testid={`staff-chip-${s.staff_id}`}
                >
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">{s.staff_name}</span>
                  <span className="text-xs text-green-600">({s.staff_id})</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveStaff(s.staff_id)}
                    className="w-6 h-6 rounded-full bg-green-100 hover:bg-red-200 flex items-center justify-center text-green-700 hover:text-red-700 text-sm"
                    data-testid={`staff-remove-${s.staff_id}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bundle Scanning */}
        {staffList.length > 0 && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <Scan className="w-5 h-5 inline mr-2" />
              Scan Bundle
            </h2>
            
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <Label>Bundle Barcode</Label>
                <Input
                  ref={bundleInputRef}
                  value={bundleCode}
                  onChange={(e) => setBundleCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                  placeholder="Scan bundle barcode"
                  className="mt-1"
                  data-testid="bundle-code-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleBundleScan}
                  disabled={loading || !bundleCode}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="bundle-validate-btn"
                >
                  Validate
                </Button>
              </div>
            </div>

            {/* Bundle Details */}
            {bundleDetails && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-[#1A1F2E] mb-2">Bundle Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">Bundle Code:</span>
                    <span className="ml-2 font-medium">{bundleDetails.bundle_code}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">SKU:</span>
                    <span className="ml-2 font-medium">{bundleDetails.sku_code}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">USN Count:</span>
                    <span className="ml-2 font-medium">{bundleDetails.usn_count}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Velocity Class:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                      bundleDetails.velocity_class === 'A' ? 'bg-green-100 text-green-700' :
                      bundleDetails.velocity_class === 'B' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {bundleDetails.velocity_class || 'C'}
                    </span>
                  </div>
                  <div className="col-span-2" data-testid="bundle-fg-name">
                    <span className="text-[#5A5F6E]">FG Name:</span>
                    <span className="ml-2 font-semibold text-[#2D5F4F]">{bundleDetails.fg_name || bundleDetails.sku_code}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bin Location */}
        {bundleDetails && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">
              <MapPin className="w-5 h-5 inline mr-2" />
              Bin Location
            </h2>

            {suggestedBin && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800">
                      Suggested Bin: <strong>{suggestedBin.bin_code}</strong>
                    </span>
                  </div>
                  {suggestedBin.capacity > 0 && (
                    <span className={`text-sm font-medium px-2 py-1 rounded ${
                      suggestedBin.available_slots === 0 
                        ? 'bg-red-100 text-red-700' 
                        : suggestedBin.available_slots <= 2 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                    }`}>
                      {suggestedBin.current_stock}/{suggestedBin.capacity} bundles ({suggestedBin.available_slots} available)
                    </span>
                  )}
                </div>
                {suggestedBin.suggestion_reason && (
                  <p className="text-xs text-green-700 italic">
                    Reason: {suggestedBin.suggestion_reason}
                  </p>
                )}
              </div>
            )}

            {!suggestedBin && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-800">
                    No bin suggestion available. Please enter bin location manually.
                  </span>
                </div>
              </div>
            )}

            {staffList.length > 1 && bundleDetails && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="sfg-split-preview">
                <p className="text-xs text-purple-700 font-medium mb-2">Split across {staffList.length} staff</p>
                <div className="space-y-1">
                  {(() => {
                    const total = bundleDetails.usn_count || 0;
                    const base = Math.floor(total / staffList.length);
                    const rem = total - base * staffList.length;
                    return staffList.map((s, i) => (
                      <div key={s.staff_id} className="flex items-center justify-between text-sm" data-testid={`sfg-split-${s.staff_id}`}>
                        <span className="text-[#5A5F6E]">{s.staff_name} <span className="text-xs text-gray-400">{s.staff_id}</span></span>
                        <span className="font-bold text-purple-700">{base + (i < rem ? 1 : 0)} pcs</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Confirm/Override Bin Location</Label>
                <Input
                  ref={binInputRef}
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handlePutAway)}
                  placeholder="Scan or enter bin location"
                  className="mt-1"
                  data-testid="bin-location-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Put Away Button */}
        {bundleDetails && binLocation && (
          <Button
            onClick={handlePutAway}
            disabled={loading || putAwaySuccess}
            className={`w-full h-14 text-lg ${
              putAwaySuccess 
                ? 'bg-green-500 hover:bg-green-500' 
                : 'bg-[#2D5F4F] hover:bg-[#234A3D]'
            } text-white`}
            data-testid="putaway-btn"
          >
            {putAwaySuccess ? (
              <>
                <CheckCircle className="w-6 h-6 mr-2" />
                Put Away Successful!
              </>
            ) : (
              <>
                <Package className="w-6 h-6 mr-2" />
                Confirm Put-Away
              </>
            )}
          </Button>
        )}

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Instructions</h3>
          <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
            <li>Scan your Staff ID to begin</li>
            <li>Scan the bundle barcode</li>
            <li>System will suggest optimal bin based on SKU velocity class</li>
            <li>Confirm or override the bin location</li>
            <li>Click "Confirm Put-Away" to complete</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default SFGPutAway;
